"use client";

import { useState, useRef, useEffect } from "react";
import styles from "./select.module.css"; // You can customize this
import { IconSelect } from "@/assets/icons/critical";
import { useLanguage } from "@/context/LanguageContext";

export default function Select({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Select...",
  hasBorder = true,
  arrowColor,
  className = "",
  afterLabel = null,
  dropdownMaxHeight,
}) {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [lastKeyTime, setLastKeyTime] = useState(0);
  const selectRef = useRef(null);

  const handleToggle = () => setIsOpen(!isOpen);

  const handleOptionClick = (option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm(""); // Clear search term when option is selected
  };

  const handleClickOutside = (e) => {
    if (selectRef.current && !selectRef.current.contains(e.target)) {
      setIsOpen(false);
      setSearchTerm(""); // Clear search term when closing
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;

    const currentTime = Date.now();
    const timeDiff = currentTime - lastKeyTime;

    // Reset search term if more than 1 second has passed
    if (timeDiff > 1000) {
      setSearchTerm("");
    }

    // Only handle letter keys (a-z, A-Z)
    if (/^[a-zA-Z]$/.test(e.key)) {
      e.preventDefault();
      const newSearchTerm = searchTerm + e.key.toLowerCase();
      setSearchTerm(newSearchTerm);
      setLastKeyTime(currentTime);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, searchTerm, lastKeyTime, options]);

  const buttonStyle = hasBorder ? {} : { border: 'none' };

  // Filter options based on search term (keep group headers that have matches)
  const filteredOptions = searchTerm
    ? (() => {
        const term = searchTerm.toLowerCase();
        const matched = [];
        let pendingGroup = null;
        for (const option of options) {
          if (option.group) {
            pendingGroup = option;
            continue;
          }
          if (option.label.toLowerCase().includes(term)) {
            if (pendingGroup) {
              matched.push(pendingGroup);
              pendingGroup = null;
            }
            matched.push(option);
          }
        }
        return matched;
      })()
    : options;

  return (
    <div
      ref={selectRef}
      className={styles.selectWrapper}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
      onMouseUp={e => e.stopPropagation()}
    >
        <button
          type="button"
          className={`${styles.selectButton} ${className}`}
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          style={buttonStyle}
        >
            <span>{options.find((o) => o.value === value)?.label || placeholder}</span>
            {afterLabel}
            <IconSelect rotation={isOpen ? 90 : 0} color={arrowColor} />
        </button>
        {isOpen && (
            <ul
              className={styles.optionsList}
              style={
                dropdownMaxHeight
                  ? { maxHeight: dropdownMaxHeight }
                  : undefined
              }
              onClick={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
              onMouseUp={e => e.stopPropagation()}
            >
              {searchTerm && (
                <li className={styles.searchIndicator}>
                  {t('common.search_indicator')
                    .replace('{term}', searchTerm)
                    .replace('{count}', String(filteredOptions.filter(o => !o.group).length))}
                </li>
              )}
              {filteredOptions.map((option, index) => (
                  <li
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!option.disabled) {
                        handleOptionClick(option);
                      }
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className={`${styles.option} ${
                        option.value === value ? styles.active : ""
                    } ${option.disabled ? styles.disabled : ""} ${
                        option.group ? styles.group : ""
                    }`}
                    >
                    {option.label}
                  </li>
              ))}
              {searchTerm && filteredOptions.length === 0 && (
                <li className={styles.noResults}>
                  Nieko nerasta &ldquo;{searchTerm}&rdquo;
                </li>
              )}
            </ul>
        )}
    </div>
  );
}